<template>
  <span><!-- A renderless component that manages chat integrations --></span>
</template>

<script setup>
/**
 * Documentation links:
 * - ProductFruits: https://help.productfruits.com/en/article/custom-chat-widget-integration
 * - Zoho Chat: https://www.zoho.com/salesiq/help/developer-section/js-api-chat-window-visible.html
 */

import * as Sentry from '@sentry/vue'
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue'

import featureNames from '@/lib/featureNames'

const $features = inject('features')
const $auth = inject('auth')

// Reactive state
const chatInitialized = ref(false)
const zohoLoaded = ref(false)

// Properties
const askAiId = import.meta.env.VITE_APP_ASKAI_ID || null
const zohoChatFeature = computed(() => $features.feature(featureNames.ZOHO_CHAT))
const productFruitsFeature = computed(() => $features.feature(featureNames.PRODUCTFRUITS))

const isAskAiChatEnabled = Boolean(askAiId)
const isZohoChatEnabled = computed(() => zohoChatFeature.value?.isActive)
const isLifeRingEnabled = computed(
  () => productFruitsFeature.value?.isActive && productFruitsFeature.value.config.showLifeRing
)

// Methods
const loadZohoChat = () => {
  try {
    const { widgetCode, scriptId } = zohoChatFeature.value.config
    window.$zoho = window.$zoho || {}
    window.$zoho.salesiq = window.$zoho.salesiq || {
      widgetcode: widgetCode,
      values: {},
      ready: () => {},
    }

    // Configure Zoho callbacks before loading the script
    window.$zoho.salesiq.ready = () => {
      zohoLoaded.value = true

      if (isLifeRingEnabled.value) {
        setupZohoChatForProductFruits()
      }

      // Should be always there if ChatManager is used how it is currently supposed to be.
      // But just in case it's used
      if ($auth?.user) {
        window.$zoho.salesiq.visitor.name($auth.user.name)
        window.$zoho.salesiq.visitor.email($auth.user.email)
        window.$zoho.salesiq.visitor.contactnumber($auth.user.phone_number)
      }
    }

    // Load Zoho script
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.id = scriptId
    script.defer = true
    script.src = 'https://salesiq.zoho.eu/widget'

    const firstScript = document.getElementsByTagName('script')[0]
    firstScript.parentNode.insertBefore(script, firstScript)
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        component: 'ChatManager',
        action: 'loadZohoChat',
      },
    })
  }
}

const loadAskAiChat = () => {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.id = askAiId
  script.defer = true
  script.src = `https://myaskai.com/api/1.1/wf/embed-v2?i=${askAiId}`

  script.onload = () => {
    // After AskAI is loaded, we can load Zoho Chat
    // AskAI will control when to show Zoho
    if (isZohoChatEnabled.value) {
      loadZohoChat()
    }
  }

  const t = document.getElementsByTagName('script')[0]
  t.parentNode.insertBefore(script, t)
}

/**
 * Must be only called after Zoho is loaded and ready.
 */
const setupZohoChatForProductFruits = () => {
  // Listen for ProductFruits Life Ring button initialization
  window.addEventListener('productfruits_button_ext_widget_init', openZohoChat)

  // Hide default Zoho button
  window.$zoho.salesiq.floatbutton.visible('hide')
  window.$zoho.salesiq.floatbutton.coin.hidetooltip()

  // Set up close/minimize handlers
  window.$zoho.salesiq.floatwindow.close(handleZohoChatClosed)
  window.$zoho.salesiq.floatwindow.minimize(handleZohoChatClosed)

  // Ensure it's hidden on load as ProductFruits Life Ring is shown
  closeZohoChat()
}

const openZohoChat = () => {
  window.$zoho.salesiq.floatwindow.visible('show')
}

const closeZohoChat = () => {
  window.$zoho.salesiq.floatwindow.visible('hide')
}

const handleZohoChatClosed = () => {
  showProductFruitsLifeRing()
}

const showProductFruitsLifeRing = () => {
  if (isLifeRingEnabled.value) {
    window.productFruits.api.inAppCenter.show()
  }
}

const initializeChatWidgets = () => {
  if (isAskAiChatEnabled) {
    loadAskAiChat()
  } else if (isZohoChatEnabled.value) {
    // Load Zoho chat only if AskAI is not enabled.
    // Otherwise Zoho chat will be loaded after AskAI is initialized.
    loadZohoChat()
  }

  chatInitialized.value = true
}

// Watch for changes in features.loaded
watch(
  () => $features.loaded,
  (isLoaded) => {
    if (isLoaded && !chatInitialized.value) {
      initializeChatWidgets()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  window.removeEventListener('productfruits_button_ext_widget_init', openZohoChat)
})
</script>
